/* eslint-disable react-refresh/only-export-components */
import './dashboard.css';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  getActiveImportKinds,
  useImportStatus,
} from '@/hooks/integrations/useImportStatus';
import { useRecentImportItems } from '@/hooks/integrations/useRecentImportItems';
import { useOnboardingStatusQuery } from '@/hooks/useOnboardingStatusQuery';
import * as bootMetrics from '@/lib/boot/bootMetrics';
import { LazyRouteBoundary } from '@/lib/components/LazyRouteBoundary';
import { isTransientError } from '@/lib/errors';
import { apiPost } from '@/lib/http/client';
import { setPersistedOnboardingComplete } from '@/lib/localStorage/persistedOnboardingComplete';
import { logger } from '@/lib/logger';
import { track } from '@/lib/mixpanel';
import type { OnboardingStatus } from '@/lib/onboarding/service';
import { onboardingService } from '@/lib/onboarding/service';
import { ensureDashboardPrefetch } from '@/lib/prefetch/entries/dashboard';
import type { ProposalListItem } from '@/lib/proposals/core/types';
import { useMyPendingProposalsPaginated } from '@/lib/proposals/hooks/useProposals';
import { useRejectProposal } from '@/lib/proposals/query/mutations';
import { proposalListQuery } from '@/lib/proposals/query/queries';
import { useViewHealthMarker } from '@/lib/view-health';

import { useAuthInfo } from '../../allauth_auth/hooks';
import { useProposalSidebar } from '../../contexts/ActionSidebarContext';
import { useOrg } from '../../contexts/OrgContext';
import { useFeatureAccess } from '../../hooks/usePermissions';
import { useTitle } from '../../hooks/useTitle';
// The dashboard body is the index route's only content, so it is imported
// statically: a lazy split here bought nothing and cost a chunk round-trip plus
// a second skeleton stage on every cold load.
import DashboardHomeComponent from './components/DashboardHome';
import { DashboardHomeSkeleton } from './components/DashboardHomeSkeleton';
import { useDashboardData } from './hooks/useDashboardData';

// Lazy load CsvImportWizard for code splitting
const CsvImportWizard = lazy(() =>
  import('@/components/csv-import/CsvImportWizard').then(m => ({
    default: m.CsvImportWizard,
  }))
);

/** Discriminated union for dashboard onboarding states */
export type DashboardOnboardingState =
  | { kind: 'loading' }
  | { kind: 'connect-email' }
  | { kind: 'imports-starting' }
  | { kind: 'processing' }
  | { kind: 'enriching' }
  | { kind: 'normal' };

function isProcessingState(
  state: DashboardOnboardingState
): state is Extract<
  DashboardOnboardingState,
  { kind: 'processing' | 'enriching' | 'imports-starting' }
> {
  return (
    state.kind === 'processing' ||
    state.kind === 'enriching' ||
    state.kind === 'imports-starting'
  );
}

const LOADING_STATE: DashboardOnboardingState = { kind: 'loading' };
const CONNECT_EMAIL_STATE: DashboardOnboardingState = { kind: 'connect-email' };
const IMPORTS_STARTING_STATE: DashboardOnboardingState = {
  kind: 'imports-starting',
};
const PROCESSING_STATE: DashboardOnboardingState = { kind: 'processing' };
const ENRICHING_STATE: DashboardOnboardingState = { kind: 'enriching' };
const NORMAL_STATE: DashboardOnboardingState = { kind: 'normal' };

/** Stable empty list returned while onboarding actions data has not loaded. */
const EMPTY_ONBOARDING_ACTIONS: ProposalListItem[] = [];

/** Input types for state derivation */
export interface OnboardingQueryState {
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  data: OnboardingStatus | undefined;
  refetch?: () => void;
}

export interface ImportStatusState {
  isLoading: boolean;
  hasActiveImports: boolean;
}

/**
 * Pure function to derive dashboard onboarding state.
 * No side effects - Date.now() and refs live in the wrapper hook.
 */
export function deriveDashboardState(
  onboardingQuery: OnboardingQueryState,
  importStatus: ImportStatusState
): DashboardOnboardingState {
  // Loading guard
  if (onboardingQuery.isLoading || importStatus.isLoading) {
    return LOADING_STATE;
  }

  // Distinguish a transient blip from a real failure. A transient error must
  // never reset a (possibly already-connected) user to the connect-email state:
  // keep last-known data when we have it, otherwise stay in loading.
  if (onboardingQuery.isError) {
    if (isTransientError(onboardingQuery.error)) {
      if (!onboardingQuery.data) {
        return LOADING_STATE;
      }
      // else: fall through and derive from last-known data
    } else {
      return CONNECT_EMAIL_STATE;
    }
  } else if (!onboardingQuery.data) {
    return CONNECT_EMAIL_STATE;
  }

  const status = onboardingQuery.data;

  // Race condition rule: hasActiveImports overrides email.connected
  if (importStatus.hasActiveImports) {
    return PROCESSING_STATE;
  }

  // Already complete - normal dashboard
  if (status.pipeline_complete) {
    return NORMAL_STATE;
  }

  // No email integration connected (gmail / testemail / any future provider) —
  // surface the connect card.
  //
  // Defensive shape handling: tolerate the legacy ``services.gmail.connected``
  // shape that may still live in stale React Query caches from before the
  // backend reshape (services.email/services.calendar landed in commit
  // 321a1aaf15 / 75b2891ab9 / etc).
  //
  // REMOVAL: drop the ``services.gmail`` fallback 30 days after this PR ships
  // — by then every long-lived browser tab will have refetched at least once
  // and re-cached the new shape.  Tracked in
  // docs/technical-debt/ONBOARDING_PROVIDER_AGNOSTIC_FOLLOWUPS.md.
  const services = status.services as
    | OnboardingStatus['services']
    | { gmail?: { connected?: boolean }; calendar?: { connected?: boolean } };
  const emailConnected =
    (services as OnboardingStatus['services']).email?.connected ??
    (services as { gmail?: { connected?: boolean } }).gmail?.connected ??
    false;
  if (!emailConnected) {
    return CONNECT_EMAIL_STATE;
  }

  // Email integration connected, check enrichment status.
  // enrichment_pending === null means "not yet computed" which is pending
  if (
    status.enrichment_pending === true ||
    status.enrichment_pending === null
  ) {
    return ENRICHING_STATE;
  }

  // Email integration connected, no active imports, enrichment not pending —
  // brief window between connect and imports starting.
  return IMPORTS_STARTING_STATE;
}

/**
 * Wrapper hook that adds timeout and retry logic to the pure derivation.
 */
export function useDashboardOnboardingState(
  onboardingQuery: OnboardingQueryState & { refetch: () => void },
  importStatus: ImportStatusState
): DashboardOnboardingState {
  // Refs for timeout tracking
  const importsStartedRef = useRef<number | null>(null);
  const enrichingStartedRef = useRef<number | null>(null);

  // State for pipeline_complete race condition retry
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  // Force re-render for timeout checks
  const [, forceUpdate] = useState(0);

  // Get base state from pure derivation.
  // Depend on individual fields instead of the wrapper objects (which the
  // caller recreates every render) so this memo stays stable across re-renders
  // that don't change the underlying data.
  const baseState = useMemo(
    () => deriveDashboardState(onboardingQuery, importStatus),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onboardingQuery.isLoading,
      onboardingQuery.isError,
      onboardingQuery.error,
      onboardingQuery.data,
      importStatus.isLoading,
      importStatus.hasActiveImports,
    ]
  );

  // Track when we enter imports-starting state
  useEffect(() => {
    if (baseState.kind === 'imports-starting') {
      if (importsStartedRef.current === null) {
        importsStartedRef.current = Date.now();
      }
    } else {
      importsStartedRef.current = null;
    }
  }, [baseState.kind]);

  // Track when we enter enriching state
  useEffect(() => {
    if (baseState.kind === 'enriching') {
      if (enrichingStartedRef.current === null) {
        enrichingStartedRef.current = Date.now();
      }
    } else {
      enrichingStartedRef.current = null;
    }
  }, [baseState.kind]);

  // 10-second timeout for imports-starting
  useEffect(() => {
    if (baseState.kind !== 'imports-starting' || !importsStartedRef.current) {
      return;
    }

    const elapsed = Date.now() - importsStartedRef.current;
    const remaining = 10_000 - elapsed;

    if (remaining <= 0) {
      forceUpdate(n => n + 1);
      return;
    }

    const timeoutId = setTimeout(() => {
      forceUpdate(n => n + 1);
    }, remaining);

    return () => clearTimeout(timeoutId);
  }, [baseState.kind]);

  // 60-second timeout for enriching
  useEffect(() => {
    if (baseState.kind !== 'enriching' || !enrichingStartedRef.current) {
      return;
    }

    const elapsed = Date.now() - enrichingStartedRef.current;
    const remaining = 60_000 - elapsed;

    if (remaining <= 0) {
      forceUpdate(n => n + 1);
      return;
    }

    const timeoutId = setTimeout(() => {
      forceUpdate(n => n + 1);
    }, remaining);

    return () => clearTimeout(timeoutId);
  }, [baseState.kind]);

  // Handle pipeline_complete + zero steps race condition
  const pipelineComplete = onboardingQuery.data?.pipeline_complete;
  const stepsLength = onboardingQuery.data?.steps?.length;
  const { refetch } = onboardingQuery;

  // Reset retryCount when the race condition is resolved
  // (steps appear or pipeline_complete changes)
  useEffect(() => {
    if (!pipelineComplete || (stepsLength && stepsLength > 0)) {
      setRetryCount(0);
    }
  }, [pipelineComplete, stepsLength]);

  useEffect(() => {
    if (pipelineComplete && stepsLength === 0 && retryCount < MAX_RETRIES) {
      const timeoutId = setTimeout(() => {
        setRetryCount(c => c + 1);
        refetch();
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [pipelineComplete, stepsLength, refetch, retryCount]);

  // Apply timeout overrides
  if (baseState.kind === 'imports-starting' && importsStartedRef.current) {
    if (Date.now() - importsStartedRef.current > 10_000) {
      return NORMAL_STATE;
    }
  }

  if (baseState.kind === 'enriching' && enrichingStartedRef.current) {
    if (Date.now() - enrichingStartedRef.current > 60_000) {
      return NORMAL_STATE;
    }
  }

  // Handle pipeline_complete race: if we've hit max retries, go to normal
  if (
    onboardingQuery.data?.pipeline_complete &&
    onboardingQuery.data?.steps?.length === 0 &&
    retryCount >= MAX_RETRIES
  ) {
    return NORMAL_STATE;
  }

  return baseState;
}

/**
 * DashboardHome - Main dashboard page
 */
const DashboardHome: React.FC = () => {
  const { currentOrg } = useOrg();
  const { user } = useAuthInfo();
  const { openProposal } = useProposalSidebar();
  const [error, setError] = useState<string | null>(null);
  const [isCsvWizardOpen, setIsCsvWizardOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  useTitle('Dashboard');

  // Track dashboard visit. The ref guard ensures the POST fires exactly
  // once per mount, even under React StrictMode's dev-only double-mount.
  const hasTrackedVisitRef = useRef(false);
  useEffect(() => {
    if (hasTrackedVisitRef.current) {
      return;
    }
    hasTrackedVisitRef.current = true;
    apiPost('/users/api/dashboard-visit/', {}).catch(err =>
      logger.warn('Dashboard visit tracking failed', { error: err })
    );
  }, []);

  // Viewer-seat feature gates — gate the proposals fetch for users without ai_actions.
  const { hasFeature } = useFeatureAccess();

  // Fetch all dashboard data
  const { calendar, actions, metrics, greeting, firstName } = useDashboardData(
    currentOrg?.id,
    user,
    { actionsEnabled: hasFeature('ai_actions') }
  );

  // Onboarding hook chain - moved from AIActionsPanel for dashboard-wide state
  const orgIdString = currentOrg?.id ? String(currentOrg.id) : '';

  // Import status - only fetch when orgIdString is non-empty to avoid invalid API calls
  const importStatus = useImportStatus({
    orgId: orgIdString,
  });

  // Recent import items - only fetch when we have active imports
  const activeImportKinds = useMemo(
    () => getActiveImportKinds(importStatus.services),
    [importStatus.services]
  );
  const { data: recentImportItems } = useRecentImportItems({
    orgId: orgIdString,
    enabled: importStatus.hasActiveImports,
    activeKinds: activeImportKinds,
  });

  // Derive dashboard state from onboarding status and import status
  const { query: onboardingQuery } = useOnboardingStatusQuery(currentOrg?.id);

  // Bidirectional sync: READ localStorage above (isPersistedComplete) gates the query;
  // WRITE below persists completion once confirmed by the server.
  // Only DashboardHome writes — ProposalListPanel reads transparently via the same cache key.
  // IMPORTANT: only set via API response (pipeline_complete=true), never manually.
  useEffect(() => {
    if (currentOrg?.id && onboardingQuery.data?.pipeline_complete) {
      logger.debug('Persisting onboarding completion', {
        orgId: currentOrg.id,
        pipelineComplete: true,
      });
      setPersistedOnboardingComplete(currentOrg.id);
    }
  }, [currentOrg?.id, onboardingQuery.data?.pipeline_complete]);

  const dashboardState = useDashboardOnboardingState(
    {
      isLoading: onboardingQuery.isLoading,
      isError: onboardingQuery.isError,
      error: onboardingQuery.error,
      data: onboardingQuery.data,
      refetch: onboardingQuery.refetch,
    },
    {
      isLoading: importStatus.isLoading,
      hasActiveImports: importStatus.hasActiveImports,
    }
  );

  // Derive isProcessing boolean for child components (backward compatible boolean)
  const isProcessing = isProcessingState(dashboardState);

  useViewHealthMarker('home-dashboard-ready', {
    ready: dashboardState.kind !== 'loading',
  });

  // Keep onboardingStatus reference for downstream code that expects it
  const onboardingStatus = onboardingQuery.data;

  const queryClient = useQueryClient();

  // The prefetch script only runs on a hard load with an org in the URL. A
  // client-side arrival (from `/`, the org switcher, a redirect) would otherwise
  // reach here with every dashboard cache cold — run the same entry now. It is
  // a no-op when the hard-load path already ran it for this org.
  useEffect(() => {
    if (currentOrg?.id) {
      ensureDashboardPrefetch(currentOrg.id, queryClient);
    }
  }, [currentOrg?.id, queryClient]);

  // Boot metrics: cache-check onboarding once orgId is available.
  // Tracks the orgId we last checked against so an org switch re-fires the
  // cache-check for the new org's key (the boot summary fires at +3s, so a
  // fast switch within that window must reflect the active org).
  const onboardingCheckedOrgIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!currentOrg?.id) {
      return;
    }
    if (onboardingCheckedOrgIdRef.current === currentOrg.id) {
      return;
    }
    onboardingCheckedOrgIdRef.current = currentOrg.id;
    bootMetrics.cacheCheck('onboarding', queryClient, [
      'onboarding-status',
      currentOrg.id,
    ]);
  }, [currentOrg?.id, queryClient]);

  // Boot metrics: cache-check import once orgIdString is non-empty. Same
  // orgId-tracking pattern as onboarding above.
  const importCheckedOrgIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!orgIdString) {
      return;
    }
    if (importCheckedOrgIdRef.current === orgIdString) {
      return;
    }
    importCheckedOrgIdRef.current = orgIdString;
    bootMetrics.cacheCheck('import', queryClient, [
      'import-status',
      orgIdString,
    ]);
  }, [orgIdString, queryClient]);

  // Boot metrics: emit dashboard-ready mark when state first reaches 'normal'.
  // The ref guarantees we emit at most once per mount, so later transitions
  // back through 'normal' won't re-emit. We intentionally do not gate by
  // elapsed time — slow loads are exactly the ones we want telemetry for.
  const dashboardReadyMarkedRef = useRef(false);
  useEffect(() => {
    if (dashboardReadyMarkedRef.current) {
      return;
    }
    if (dashboardState.kind !== 'normal') {
      return;
    }
    dashboardReadyMarkedRef.current = true;
    bootMetrics.mark('dashboard-ready', { kind: 'normal' });
  }, [dashboardState.kind]);

  // Perf trace: when each onboarding-state kind is first reached, so the boot
  // timeline shows how long the 'loading' gate held the whole page.
  useEffect(() => {
    bootMetrics.markOnce(`stage:dashboard-state:${dashboardState.kind}`);
  }, [dashboardState.kind]);

  // Check if there are pending onboarding actions based on filter_tag_counts
  // This is the source of truth for whether to show only onboarding actions
  const hasOnboardingActions = actions.counts.onboarding > 0;

  const aiActionsEnabled = hasFeature('ai_actions');

  // Onboarding actions query - fetch when onboarding actions exist
  // (needed because onboarding actions may not be in the main paginated list)
  const { data: onboardingActionsData, isLoading: onboardingProposalsLoading } =
    useMyPendingProposalsPaginated(currentOrg?.id || 0, 1, 50, 'onboarding', {
      enabled: !!currentOrg?.id && hasOnboardingActions && aiActionsEnabled,
    });

  const onboardingActions: ProposalListItem[] =
    onboardingActionsData?.results ?? EMPTY_ONBOARDING_ACTIONS;

  // Total onboarding proposals (all resolutions) — used to detect if user
  // has completed any by comparing against the pending count above.
  const { data: allOnboardingData } = useQuery({
    ...proposalListQuery(currentOrg?.id || 0, {
      filter_tags__contains: 'onboarding',
      assigned_to_me: true,
      page_size: 1,
    }),
    enabled: !!currentOrg?.id && hasOnboardingActions && aiActionsEnabled,
  });

  const hasStartedOnboarding =
    allOnboardingData != null &&
    onboardingActions.length < (allOnboardingData.count ?? 0);

  // The first proposals are gated on the user creating their deal pipeline: the
  // backend parks the create_proposals step in the non-error WAITING state until
  // a pipeline exists. Surface that so the onboarding "analyzing" state explains
  // the wait.
  const isWaitingForPipeline = (onboardingQuery.data?.steps ?? []).some(
    step => step.step_name === 'create_proposals' && step.status === 'waiting'
  );

  // Reject mutation for skipping onboarding actions
  const rejectMutation = useRejectProposal(currentOrg?.id || 0);
  // Destructure `mutate` so useCallback can depend on the stable function
  // reference rather than the full mutation object (which changes on each
  // idle→pending→success transition and would recreate the callback unnecessarily).
  const { mutate: rejectProposal } = rejectMutation;

  // Auto-complete onboarding when imports finish.
  const hasAutoCompletedRef = useRef(false);
  useEffect(() => {
    // Guard: need onboarding status
    if (!onboardingStatus || onboardingStatus.onboarding.is_completed) {
      return;
    }

    // Wait for all data to be ready
    if (
      onboardingProposalsLoading ||
      importStatus.isLoading ||
      importStatus.hasActiveImports ||
      onboardingStatus.enrichment_pending !== false
    ) {
      return;
    }

    // Auto-complete onboarding (guard against double-fire)
    if (!hasAutoCompletedRef.current) {
      if (!currentOrg?.id) {
        return;
      }
      hasAutoCompletedRef.current = true;
      setError(null);
      onboardingService.updateStep(currentOrg.id, 'complete').catch(err => {
        logger.warn('Failed to auto-complete onboarding:', {
          error: err,
          orgId: currentOrg?.id,
        });
        setError(
          "Couldn't mark your onboarding as complete. Try again or refresh the page."
        );
      });
      track('onboarding_auto_completed', {
        orgId: currentOrg.id,
        reason:
          onboardingActions.length === 0
            ? 'all_actions_completed_in_inbox'
            : 'imports_finished',
      });
    }
  }, [
    onboardingStatus,
    importStatus.hasActiveImports,
    importStatus.isLoading,
    onboardingProposalsLoading,
    onboardingActions.length,
    currentOrg?.id,
  ]);

  const handleStartAction = useCallback(
    (proposalId: number, contextIds: number[]) => {
      openProposal(proposalId, contextIds);
    },
    [openProposal]
  );

  const handleSkipAction = useCallback(
    (proposalId: number): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        rejectProposal(
          { proposalId, request: { reason: 'Skipped during onboarding' } },
          {
            onSuccess: () => {
              track('onboarding_action_skipped', {
                orgId: currentOrg?.id,
                proposalId,
              });
              resolve();
            },
            onError: reject,
          }
        );
      }),
    [rejectProposal, currentOrg?.id]
  );

  const handleOpenCsvWizard = useCallback(() => {
    setIsCsvWizardOpen(true);
  }, []);

  const handleCloseCsvWizard = useCallback(() => {
    setIsCsvWizardOpen(false);
  }, []);

  // Org not resolved yet (cold first visit; a revisit hydrates it from
  // localStorage before the first render). This is the ONE place the whole-page
  // skeleton renders; once the org is known the real header takes over and
  // only the sections below it can still be skeletons.
  if (!currentOrg) {
    return (
      <DashboardHomeSkeleton
        inputValue={inputValue}
        onInputChange={handleInputChange}
        stage='org-loading'
      />
    );
  }

  return (
    <>
      <LazyRouteBoundary name='dashboard-home'>
        <DashboardHomeComponent
          dashboardState={dashboardState}
          isProcessing={isProcessing}
          importStatus={importStatus}
          recentImportItems={recentImportItems}
          hasOnboardingActions={hasOnboardingActions}
          onboardingActions={onboardingActions}
          onStartAction={handleStartAction}
          onSkipAction={handleSkipAction}
          hasStartedOnboarding={hasStartedOnboarding}
          isWaitingForPipeline={isWaitingForPipeline}
          onUploadContacts={handleOpenCsvWizard}
          calendar={calendar}
          actions={actions}
          metrics={metrics}
          greeting={greeting}
          firstName={firstName}
          orgId={currentOrg.id}
          inputValue={inputValue}
          onInputChange={handleInputChange}
        />
      </LazyRouteBoundary>

      {/* CSV Import Wizard (triggered by "Upload contacts" quick prompt). Mounted
          only when open so its chunk is fetched on demand, not at dashboard boot. */}
      {isCsvWizardOpen && (
        <LazyRouteBoundary name='csv-import-wizard'>
          <CsvImportWizard
            orgId={currentOrg.id}
            isOpen={isCsvWizardOpen}
            onClose={handleCloseCsvWizard}
          />
        </LazyRouteBoundary>
      )}

      {/* Error display */}
      {error && (
        <div className='fixed bottom-4 right-4 bg-error-primary text-white px-4 py-2 rounded-lg shadow-lg z-50'>
          {error}
        </div>
      )}
    </>
  );
};

export default DashboardHome;
